import { GlobalAccountRoleType, InviteStatus, PersonOrgType, UnencryptedData } from '@ndvr/auth-api';

import { TagTypes } from 'widgets/Tag';

type NameValue = UnencryptedData | { plainText?: string } | string | undefined;

const normalizeNameValue = (value: NameValue) => (typeof value === 'string' ? value : value?.plainText);

/**
 * Returns the name of the person if firstName or lastName is present.
 * Returns '' otherwise.
 *
 * @param personInfo input person
 * @returns name of the person
 */
export const getPersonName = (personInfo?: { firstName?: NameValue; lastName?: NameValue }, isLoading?: boolean) => {
    if (isLoading) {
        return '...';
    }

    const firstName = normalizeNameValue(personInfo?.firstName);
    const lastName = normalizeNameValue(personInfo?.lastName);
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    }
    // we shouldn't have users without name (especially in prod), but let's keep it on the safe side to have something to show
    return firstName || lastName || 'Anonymous User';
};

/**
 * Returns the initials of the person if firstName or lastName is present.
 * Returns '' otherwise.
 *
 * @param personInfo input person
 * @returns initials of the person
 */
export const getPersonInitials = (personInfo?: { firstName?: NameValue; lastName?: NameValue }) => {
    const firstName = normalizeNameValue(personInfo?.firstName);
    const lastName = normalizeNameValue(personInfo?.lastName);
    const firstInitial = firstName?.substring(0, 1);
    const lastInitial = lastName?.substring(0, 1);
    return `${firstInitial ?? ''}${lastInitial ?? ''}`;
};

export const getPersonPrimaryEmail = (personOrgInfo?: { primaryEmail?: UnencryptedData }) => {
    return personOrgInfo?.primaryEmail?.plainText;
};

export const getOrgName = (personOrgInfo?: { orgName?: NameValue }) =>
    normalizeNameValue(personOrgInfo?.orgName) || 'Unnamed Organization';

export const getPersonOrgName = (personOrg?: {
    personOrgType: PersonOrgType;
    orgName?: NameValue;
    firstName?: NameValue;
    lastName?: NameValue;
}) => {
    if (!personOrg) {
        return 'Anonymous';
    }
    if (personOrg.orgName) {
        return getOrgName(personOrg);
    }
    if (!personOrg.orgName && personOrg.personOrgType === PersonOrgType.JOINT_PERSONS) {
        return 'Unnamed Joint Account';
    }
    return getPersonName(personOrg);
};

/**
 * Gets the display name for a person or organization, checking orgName first before falling back to person name.
 * Works with plain string fields (not UnencryptedData).
 * Useful for API responses that return plain strings instead of encrypted data objects.
 *
 * @param owner - Object with orgName, firstName, middleName, and lastName as plain strings
 * @returns Display name for the person or organization
 */
export const getAccountOwnerName = (owner?: {
    orgName?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
}): string => {
    if (owner?.orgName) {
        return owner.orgName;
    }
    return getPersonName({
        firstName: [owner?.firstName, owner?.middleName].filter(Boolean).join(' ').trim(),
        lastName: owner?.lastName,
    });
};

export const PERSON_STATUS_TAG_MAP: { [key in InviteStatus]: TagTypes } = {
    [InviteStatus.INVITED]: 'info',
    [InviteStatus.INVITE_EXPIRED]: 'negative',
    [InviteStatus.REGISTERED]: 'positive',
    [InviteStatus.NOT_INVITED]: 'neutral',
};

const GLOBAL_ACCOUNT_ROLE_MAP = {
    [GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER]: 'Principal',
    [GlobalAccountRoleType.SECONDARY_USER]: 'Secondary User',
};

export const getGlobalAccountRoleDisplayLabel = (role: {
    roleType: GlobalAccountRoleType;
    roleLabel?: string;
}): string => {
    return role.roleType === GlobalAccountRoleType.SECONDARY_USER
        ? role.roleLabel || 'Member'
        : GLOBAL_ACCOUNT_ROLE_MAP[role.roleType];
};

// Splits an extracted full name into first/last form fields: the first token becomes the first
// name, the remainder (if any) the last name.
export const splitName = (fullName: string): { firstName: string; lastName: string } => {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length <= 1) {
        return { firstName: parts[0] ?? '', lastName: '' };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

// Fixed choices for a household member's role; anything else is entered as 'Custom' with a label.
export const HOUSEHOLD_ROLE_OPTIONS = ['Client', 'Family', 'Lawyer', 'Tax Advisor'] as const;
